import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { MapPin, Loader2, CloudOff, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { PLAN_CLS } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";

export default function Standorte() {
  const { user } = useAuth();
  const scope = user?.role === "super_admin" ? "all" : "customers";
  const [state, setState] = useState(null);

  const load = useCallback(() => {
    setState(null);
    api.get("/organizations/summary/all-locations", { params: { scope } })
      .then((r) => setState(r.data))
      .catch(() => setState({ service_available: false, locations: [], reason: "FUNDIO Tenant Service nicht erreichbar." }));
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  if (state === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;

  const rows = state.locations || [];

  return (
    <div data-testid="standorte-page">
      <PageHeader title="Standorte" subtitle="Alle physischen Standorte der FUNDIO-Kunden aus dem kanonischen Tenant-System (read-only).">
        <button data-testid="standorte-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-fundio-border px-3 py-2 text-sm font-medium text-fundio-ink transition-colors hover:bg-fundio-subtle">
          <RefreshCw className="h-4 w-4" /> Aktualisieren
        </button>
      </PageHeader>

      {!state.service_available ? (
        <div data-testid="standorte-unavailable" className="card-surface flex flex-col items-center rounded-xl px-6 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-status-danger-bg"><CloudOff className="h-6 w-6 text-status-danger" /></span>
          <p className="mt-4 text-base font-semibold text-fundio-ink">FUNDIO Tenant Service nicht erreichbar</p>
          <p className="mt-1 text-sm text-fundio-muted">{state.reason}</p>
        </div>
      ) : (
        <Panel title={`Standorte (${rows.length})`} testid="standorte-panel">
          {!rows.length ? (
            <EmptyState icon={MapPin} title="Keine Standorte" description="Es sind derzeit keine Standorte im Tenant-System hinterlegt." testid="standorte-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-fundio-border text-left text-xs uppercase tracking-wide text-fundio-faint">
                    <th className="px-2 py-2 font-medium">Standort</th>
                    <th className="px-2 py-2 font-medium">Kunde</th>
                    <th className="px-2 py-2 font-medium">Adresse</th>
                    <th className="px-2 py-2 font-medium">Kanton</th>
                    <th className="px-2 py-2 font-medium">Telefon</th>
                    <th className="px-2 py-2 font-medium">Plan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-fundio-border">
                  {rows.map((r, i) => {
                    const l = r.location || {};
                    return (
                      <tr key={`${r.tenant_id}-${l.id || i}`} data-testid={`standort-row-${l.id || i}`} className="transition-colors hover:bg-fundio-subtle">
                        <td className="px-2 py-3 font-medium text-fundio-ink">
                          {l.name || "—"}
                          {r.is_demo && <span className="ml-2 inline-flex items-center rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-status-warning">QA</span>}
                        </td>
                        <td className="px-2 py-3 text-fundio-muted">{r.company_name || "—"}</td>
                        <td className="px-2 py-3 text-fundio-muted">{[l.street, `${l.postal_code || ""} ${l.city || ""}`.trim()].filter(Boolean).join(", ") || "—"}</td>
                        <td className="px-2 py-3 text-fundio-muted">{l.canton || "—"}</td>
                        <td className="px-2 py-3 text-fundio-muted">{l.phone || "—"}</td>
                        <td className="px-2 py-3">{r.plan ? <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${PLAN_CLS[r.plan] || PLAN_CLS.FREE}`}>{r.plan}</span> : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
